/**
 * components/GridComparisonModal.tsx
 * Read-only grid view comparing all profiles side-by-side
 *
 * Shows complete risk cascade across all profiles in a table format
 */

// 1. External dependencies
import React, { useMemo } from 'react';

// 2. Internal absolute imports
import { Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, Button, Chip } from '@enfomentis/ui';
import type { OnboardingDraft } from '@/types/transient/OnboardingDraft';
import { formatRiskDisplay } from '../utils/riskValidation';

/**
 * Risk profile type from draft
 */
type RiskProfile = NonNullable<OnboardingDraft['trading']['riskConfiguration']>['profiles'][number];

/**
 * GridComparisonModal props
 */
interface GridComparisonModalProps {
  isOpen: boolean;
  onClose: () => void;
  profiles: RiskProfile[];
  defaultProfileId: string | null;
  strategyName?: string;
  strategyTempId?: string;
  playbookNames: Map<string, string>; // tempId -> name
}

/**
 * GridComparisonModal component
 */
export const GridComparisonModal: React.FC<GridComparisonModalProps> = ({
  isOpen,
  onClose,
  profiles,
  defaultProfileId,
  strategyName,
  strategyTempId,
  playbookNames
}) => {
  // Build row data
  const rows = useMemo(() => {
    const result: Array<{ label: string; type: 'account' | 'strategy' | 'playbook'; id?: string }> = [];

    // Account default row
    result.push({ label: 'Account Default', type: 'account' });

    // Strategy override row (if strategy exists)
    if (strategyName && strategyTempId) {
      result.push({ label: `Strategy: ${strategyName}`, type: 'strategy', id: strategyTempId });
    }

    // Playbook override rows
    playbookNames.forEach((name, tempId) => {
      result.push({ label: `Playbook: ${name}`, type: 'playbook', id: tempId });
    });

    return result;
  }, [strategyName, strategyTempId, playbookNames]);

  // Get risk value for a specific profile and row
  const getRiskValue = (profile: RiskProfile, row: typeof rows[number]): string => {
    if (row.type === 'account') {
      return formatRiskDisplay(profile.accountDefault);
    }

    if (row.type === 'strategy' && row.id) {
      const override = profile.strategyOverrides?.find(o => o.strategyTempId === row.id);
      return override ? formatRiskDisplay(override.risk) : '—';
    }

    if (row.type === 'playbook' && row.id) {
      const override = profile.playbookOverrides?.find(o => o.playbookTempId === row.id);
      return override ? formatRiskDisplay(override.risk) : '—';
    }

    return '—';
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="5xl"
      scrollBehavior="inside"
    >
      <ModalContent>
        <ModalHeader>
          <h3 className="text-xl font-bold">Risk Profile Comparison</h3>
        </ModalHeader>
        <ModalBody>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b-2 border-border-primary">
                  <th className="text-left p-3 font-semibold min-w-[200px]">Hierarchy Level</th>
                  {profiles.map((profile) => (
                    <th key={profile.tempId} className="text-center p-3 font-semibold min-w-[150px]">
                      <div className="flex flex-col items-center gap-2">
                        <div className="flex items-center gap-2">
                          {profile.color && (
                            <div
                              className="w-3 h-3 rounded-full"
                              style={{ backgroundColor: profile.color }}
                            />
                          )}
                          <span>{profile.name}</span>
                        </div>
                        {profile.tempId === defaultProfileId && (
                          <Chip size="sm" variant="flat" color="primary">
                            Default
                          </Chip>
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => (
                  <tr
                    key={`${row.type}-${row.id || 'default'}`}
                    className={`border-b border-border-secondary ${
                      index % 2 === 0 ? 'bg-surface-50' : ''
                    }`}
                  >
                    <td className="p-3 font-medium">
                      <div className="flex items-center gap-2">
                        {row.type === 'account' && <span className="text-primary">●</span>}
                        {row.type === 'strategy' && <span className="text-secondary ml-4">○</span>}
                        {row.type === 'playbook' && <span className="text-default ml-8">•</span>}
                        {row.label}
                      </div>
                    </td>
                    {profiles.map((profile) => (
                      <td key={profile.tempId} className="p-3 text-center">
                        <span className={getRiskValue(profile, row) === '—' ? 'text-text-tertiary italic' : ''}>
                          {getRiskValue(profile, row)}
                        </span>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Legend */}
          <div className="mt-6 p-4 bg-surface-100 rounded-lg">
            <h4 className="text-sm font-semibold mb-2">Risk Cascade Hierarchy</h4>
            <div className="space-y-1 text-sm text-text-secondary">
              <div className="flex items-center gap-2">
                <span className="text-primary">●</span>
                <span>Account Default - Base risk for all trades</span>
              </div>
              <div className="flex items-center gap-2 ml-4">
                <span className="text-secondary">○</span>
                <span>Strategy Override - Applies to all playbooks in strategy (overrides account default)</span>
              </div>
              <div className="flex items-center gap-2 ml-8">
                <span className="text-default">•</span>
                <span>Playbook Override - Most specific (overrides strategy and account)</span>
              </div>
              <div className="mt-2 italic">
                <span className="text-text-tertiary">—</span> = No override configured, uses higher level in cascade
              </div>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onPress={onClose}>
            Close
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
